
import { mapState, mapActions, mapGetters } from 'vuex';
import TransitionExpand from '@/transitions/TransitionExpand';
import ProductOptionTypeHelper from '@/mixins/ProductOptionTypeHelper';
import GoogleTranslationHelper from '@/mixins/GoogleTranslationHelper';
import ReviewSort from '@/enums/ReviewSort';
import WidgetType from '@/enums/WidgetType';
import AiIconType from '@/enums/AiIconType';
import { pathWithParams } from '@/lib/params';
import DropdownMenuWidth from '@/enums/DropdownMenuWidth';

export default {
  name: 'AppReviewSearchBar',
  components: { TransitionExpand },
  mixins: [ProductOptionTypeHelper, GoogleTranslationHelper],
  props: {
    showDisplayByProduct: { type: Boolean, default: false },
    showSearchSection: { type: Boolean, default: true },
    params: { type: Object, default: () => ({}) },
    totalProductMediaReviewsCount: { type: Number, default: 0 }
  },
  data() {
    return {
      RATING_OPTIONS: [
        { name: this.$t('search_bar.score.5'), value: 5, disabled: false },
        { name: this.$t('search_bar.score.4'), value: 4, disabled: false },
        { name: this.$t('search_bar.score.3'), value: 3, disabled: false },
        { name: this.$t('search_bar.score.2'), value: 2, disabled: false },
        { name: this.$t('search_bar.score.1'), value: 1, disabled: false }
      ],
      displayByProduct: false,
      enableSearchButton: false,
      isSubmitStatus: false,
      searchInput: null,
      selectedCategory: null,
      selectedSortType: ReviewSort.RECENTLY_CREATED,
      selectedScores: [],
      selectedReviewOptions: {},
      selectedProductOptions: {},
      useFilterByBrandLocale: false,
      useFilterByVerifiedBuyer: false,
      isMediaReviewsFirstChecked: false,
      selectedReviewTagType: null
    };
  },
  computed: {
    ...mapState('widget', ['widget']),
    ...mapState('translation', ['locale']),
    ...mapState('window', ['windowService']),
    ...mapState('reviewSettings', ['reviewSettings']),
    ...mapState('reviewTagTypes', [
      'reviewTagTypes',
      'isLoadingReviewTagTypes'
    ]),
    ...mapGetters('widget', [
      'displayMediaReviewsFirstToggle',
      'showCategoryFilter',
      'showOptionTypeFilter',
      'showScoreFilter',
      'showFilterSection',
      'showBrandLocaleFilter',
      'showToggleFilterSection',
      'showVerifiedBuyerFilter'
    ]),
    ...mapState('reviewOptionTypes', [
      'reviewOptionTypes',
      'isLoadingReviewOptionTypes'
    ]),
    ...mapState('productCategories', ['isLoadingProductCategories']),
    ...mapGetters('productCategories', ['filterableProductCategories']),
    DropdownMenuWidth: () => DropdownMenuWidth,
    categoryOptions() {
      return this.filterableProductCategories.map(({ id, name }) => ({
        name,
        value: id,
        disabled: false
      }));
    },
    displayOrderOptions() {
      return this.widget.display_orders_in_use.map(order => ({
        value: order,
        name: ReviewSort.t(order),
        leadingAiIconName:
          order === ReviewSort.AI_RECOMMENDATION ? this.aiIconName : null
      }));
    },
    aiIconName() {
      return this.reviewSettings.ai_icon_type === AiIconType.CUSTOM
        ? 'icon-ai-solid'
        : 'icon-ai-gradation';
    },
    selectedReviewOptionKeys() {
      return Object.keys(this.selectedReviewOptions);
    },
    selectedProductOptionKeys() {
      return Object.keys(this.selectedProductOptions);
    },
    showResetButton() {
      return (
        this.selectedCategory ||
        this.selectedScores.length ||
        this.selectedReviewOptionKeys.length > 0 ||
        this.selectedProductOptionKeys.length > 0 ||
        this.selectedReviewTagType
      );
    },
    showProductOptionTypeFilter() {
      return (
        this.showOptionTypeFilter &&
        this.reviewSettings.show_options &&
        this.reviewSettings.allow_searching_product_options
      );
    },
    reviewOptionResetParams() {
      return this.selectedReviewOptionKeys.reduce(
        (accumulate, key) => ({ ...accumulate, [key]: null }),
        {}
      );
    },
    productOptionResetParams() {
      return this.selectedProductOptionKeys.reduce(
        (accumulate, key) => ({ ...accumulate, [key]: null }),
        {}
      );
    },
    reviewTagTypeOptions() {
      return this.reviewTagTypes.map(({ name, id }) => ({
        name,
        value: id
      }));
    },
    isLoadingFilterSection() {
      return (
        (this.showCategoryFilter && this.isLoadingProductCategories) ||
        (this.showOptionTypeFilter && this.isLoadingReviewOptionTypes) ||
        (this.showProductOptionTypeFilter &&
          this.isLoadingProductOptionTypes) ||
        (!!this.widget.use_review_keyword_filter &&
          this.isLoadingReviewTagTypes)
      );
    },
    isMediaReviewsFirst() {
      return (
        this.widget.widget_type === WidgetType.PRODUCT_REVIEWS &&
        (this.isMediaReviewsFirstChecked || this.widget.review_show_photo_first)
      );
    }
  },
  watch: {
    params(newParams, oldParams) {
      this.syncFilterStateFromParams();
      if (newParams.query !== oldParams.query) this.syncSearchStateFromParams();
    }
  },
  created() {
    this.syncFilterStateFromParams();
    this.syncSearchStateFromParams();
    if (this.widget.use_review_keyword_filter) this.fetchReviewTagTypes();
    if (this.showProductOptionTypeFilter)
      this.fetchProductOptionTypes({ fields: 'check_combo_product' });
    if (this.showCategoryFilter) this.fetchProductCategories();
    if (this.showOptionTypeFilter) this.fetchReviewOptionTypes();
  },
  methods: {
    ...mapActions('reviewTagTypes', ['fetchReviewTagTypes']),
    ...mapActions('productCategories', ['fetchProductCategories']),
    ...mapActions('reviewOptionTypes', ['fetchReviewOptionTypes']),
    syncSearchStateFromParams() {
      const { query } = this.params;
      this.searchInput = query || null;
      this.enableSearchButton = !!query;
      this.isSubmitStatus = !!query;
    },
    syncFilterStateFromParams() {
      const {
        sort,
        category_id,
        scores,
        review_tag_type_ids,
        filter_by_brand_locale,
        filter_by_verified_buyer,
        media_reviews_first
      } = this.params;
      this.selectedSortType = parseInt(sort || this.widget.display_order);
      this.selectedCategory = parseInt(category_id) || null;
      this.selectedScores = scores
        ? scores.split(',').map(s => parseInt(s))
        : [];
      this.selectedReviewOptions = {};
      this.parseReviewOptionTypes(this.params);
      this.selectedProductOptions = {};
      this.parseProductOptionTypes(this.params);
      this.selectedReviewTagType =
        parseInt(review_tag_type_ids?.split(',')?.[0]) || null;
      this.useFilterByBrandLocale = filter_by_brand_locale === 'true';
      this.useFilterByVerifiedBuyer = filter_by_verified_buyer === 'true';
      this.isMediaReviewsFirstChecked = media_reviews_first === 'true';
    },
    openReviewSortDialog() {
      const reviewSortPath = pathWithParams(
        `${window.location.origin}/v2/review_sort`,
        {
          locale: this.locale,
          display_orders_in_use: this.widget.display_orders_in_use,
          media_reviews_first: this.isMediaReviewsFirst
        }
      );
      this.windowService.openFullscreenPopup(reviewSortPath);
    },
    enableSearch(enable) {
      this.enableSearchButton = enable;
      if (this.enableSearchButton) {
        this.$nextTick(() => {
          this.$refs.search_input.focus();
        });
      }
    },
    resetFilters() {
      this.selectedCategory = null;
      this.selectedScores = [];
      this.selectedReviewTagType = null;
      this.submit({
        category_id: null,
        scores: [],
        review_tag_type_ids: null,
        ...this.reviewOptionResetParams,
        ...this.productOptionResetParams
      });
      this.selectedReviewOptions = {};
      this.selectedProductOptions = {};
    },
    onChangeReviewOption(id, value) {
      if (value.length) this.$set(this.selectedReviewOptions, id, value);
      else this.$delete(this.selectedReviewOptions, id);

      this.submit({ [id]: value.join(',') });
    },
    submitVerifiedBuyerFilter() {
      this.submit({ filter_by_verified_buyer: this.useFilterByVerifiedBuyer });
    },
    submitBrandLocaleFilter() {
      this.submit({ filter_by_brand_locale: this.useFilterByBrandLocale });
    },
    parseReviewOptionTypes(params) {
      Object.entries(params).forEach(([key, value]) => {
        if (key.startsWith('option_')) {
          this.$set(this.selectedReviewOptions, key, value.split(','));
        }
      });
    },
    parseProductOptionTypes(params) {
      Object.entries(params).forEach(([key, values]) => {
        if (key.startsWith('product_option_')) {
          const options = values.split(',').map(option => parseInt(option));
          this.$set(this.selectedProductOptions, key, options);
        }
      });
    },
    onChangeProductOption(id, value) {
      const productOptionKey = `product_option_${id}`;
      if (value.length)
        this.$set(this.selectedProductOptions, productOptionKey, value);
      else this.$delete(this.selectedProductOptions, productOptionKey);

      this.submit({ [productOptionKey]: value.join(',') });
    },
    submitSearchInput() {
      this.isSubmitStatus = this.searchInput?.length > 0;
      this.submit({ query: this.searchInput });
    },
    enterSearchInput() {
      if (this.enableSearchButton) {
        this.submitSearchInput();
        this.$nextTick(() => this.$refs.search_input.focus());
      }
    },
    submit(params) {
      this.$emit('submit', { ...params, page: '1' });
    }
  }
};
